import { useState } from "react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Compass, X, AlertTriangle } from "lucide-react";
import { WorkProductProgress } from "./WorkProductProgress";

export const GoalStrategies = ({ goal, strategies, canLink, onLink, memberSkills, onProgress, transferCtx }) => {
  const [pending, setPending] = useState(null);
  const have = memberSkills || new Set();
  const missingOf = (s) => s.behaviors.filter((b) => !have.has(b.id));
  const linked = (goal.strategy_ids || []).map((id) => strategies.find((s) => s.id === id)).filter(Boolean);
  const available = strategies.filter((s) => !(goal.strategy_ids || []).includes(s.id));
  const ids = linked.map((s) => s.id);
  const save = async (next) => {
    try {
      await onLink(goal.id, next);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Koppelen mislukt");
    }
  };
  const add = (sid) => {
    const s = strategies.find((x) => x.id === sid);
    if (missingOf(s).length) setPending(s);
    else save([...ids, sid]);
  };

  return (
    <div data-testid="goal-strategies">
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {linked.map((s) => {
        const gap = missingOf(s);
        return (
          <span key={s.id} className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] bg-white ${gap.length ? "border-red-600 text-red-700" : "border-zinc-900"}`} data-testid="linked-strategy-chip" data-alert={gap.length > 0} title={gap.length ? `Ontbreekt: ${gap.map((b) => b.text).join("; ")}` : s.behaviors.map((b) => `• ${b.text}`).join("\n")}>
            {gap.length ? <AlertTriangle className="h-3 w-3" /> : <Compass className="h-3 w-3" />} {s.name}
            {canLink && (
              <button onClick={() => save(ids.filter((i) => i !== s.id))} className="text-zinc-400 hover:text-red-600" data-testid="unlink-strategy-button">
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        );
      })}
      {canLink && available.length > 0 && (
        <Select value="" onValueChange={add}>
          <SelectTrigger className="rounded-none border-dashed border-zinc-400 h-6 w-auto text-[11px] px-2 gap-1" data-testid="link-strategy-select">
            <SelectValue placeholder="+ strategie" />
          </SelectTrigger>
          <SelectContent>
            {available.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {canLink && strategies.length === 0 && <span className="text-[10px] text-zinc-400">Nog geen strategieën in de repository</span>}
    </div>
      {onProgress && <WorkProductProgress goal={goal} linked={linked} canEdit={canLink} onProgress={onProgress} transferCtx={transferCtx} />}

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="rounded-none" data-testid="skill-gap-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-grotesk flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Ontbrekende vaardigheden
            </AlertDialogTitle>
            <AlertDialogDescription>
              Niemand in dit subsysteem beheerst de volgende gedragingen van strategie "{pending?.name}". Je kunt de strategie toch koppelen; de naam wordt dan rood in het model.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="space-y-1 text-sm" data-testid="skill-gap-list">
            {pending && missingOf(pending).map((b) => (
              <li key={b.id} className="border-l-2 border-red-600 bg-red-50 px-3 py-1.5">{b.text}</li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="skill-gap-cancel-button">Annuleren</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-red-700 hover:bg-red-800" onClick={() => save([...ids, pending.id])} data-testid="skill-gap-confirm-button">
              Toch koppelen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
